import { Suspense, lazy, useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { trpc, createTrpcClient } from './api/index.js'
import { ToastProvider } from './components/common/Toast.js'
import { ThemeProvider } from './components/ThemeProvider.js'
import { Sidebar } from './components/layout/Sidebar.js'
import { LoginScreen } from './components/auth/LoginScreen.js'
import { GlobalSearch } from './components/common/GlobalSearch.js'
import { useAuthStore, registerLogoutCleanup } from './stores/authStore.js'
import { useNav, type Page } from './stores/navigationStore.js'
import { Menu } from 'lucide-react'

// Lazy-loaded pages
const DashboardPage    = lazy(() => import('./pages/dashboard/DashboardPage.js'))
const ClientiPage      = lazy(() => import('./pages/clienti/ClientiPage.js'))
const EchipamentePage  = lazy(() => import('./pages/echipamente/EchipamentePage.js'))
const RezervariPage    = lazy(() => import('./pages/rezervari/RezervariPage.js'))
const ContractePage    = lazy(() => import('./pages/contracte/ContractePage.js'))
const PredareReturPage = lazy(() => import('./pages/predare-retur/PredareReturPage.js'))
const FacturarePage    = lazy(() => import('./pages/facturare/FacturarePage.js'))
const IncasariPage     = lazy(() => import('./pages/incasari/IncasariPage.js'))
const SetariPage       = lazy(() => import('./pages/setari/SetariPage.js'))
const HardwarePage     = lazy(() => import('./pages/hardware/HardwarePage.js'))
const MentenantaPage   = lazy(() => import('./pages/mentenanta/MentenantaPage.js'))
const RapoartePage     = lazy(() => import('./pages/rapoarte/RapoartePage.js'))
const CheltuieliPage   = lazy(() => import('./pages/cheltuieli/CheltuieliPage.js'))
const TransferPage     = lazy(() => import('./pages/transfer/TransferPage.js'))

const PAGE_LABELS: Record<Page, string> = {
  dashboard:     'Dashboard',
  clienti:       'Clienți',
  echipamente:   'Inventar',
  contracte:     'Contracte',
  'predare-retur': 'Predare / Retur',
  facturare:     'Facturare',
  incasari:      'Încasări',
  cheltuieli:    'Cheltuieli',
  transfer:      'Transfer',
  mentenanta:    'Mentenanță',
  rezervari:     'Rezervări',
  rapoarte:      'Rapoarte',
  hardware:      'Hardware & AMEF',
  setari:        'Configurări',
}

const PAGE_SHORTCUTS: Record<string, Page> = {
  '1': 'dashboard', '2': 'clienti', '3': 'echipamente',
  '4': 'contracte', '5': 'facturare', '6': 'rezervari',
  '7': 'predare-retur', '8': 'mentenanta', '9': 'rapoarte',
}

function Placeholder({ name }: { name: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-32 text-slate-500">
      <p className="text-lg font-semibold">{name}</p>
      <p className="text-sm mt-2 text-slate-600">În curs de implementare</p>
    </div>
  )
}

function PageRouter() {
  const { page } = useNav()
  switch (page) {
    case 'dashboard':     return <DashboardPage />
    case 'clienti':       return <ClientiPage />
    case 'echipamente':   return <EchipamentePage />
    case 'rezervari':     return <RezervariPage />
    case 'contracte':     return <ContractePage />
    case 'predare-retur': return <PredareReturPage />
    case 'facturare':     return <FacturarePage />
    case 'incasari':      return <IncasariPage />
    case 'cheltuieli':    return <CheltuieliPage />
    case 'transfer':      return <TransferPage />
    case 'mentenanta':    return <MentenantaPage />
    case 'rapoarte':      return <RapoartePage />
    case 'hardware':      return <HardwarePage />
    case 'setari':        return <SetariPage />
    default:              return <Placeholder name={page} />
  }
}

// ─── Mobile TopBar ─────────────────────────────────────────────────────────

const ROL_AVATAR: Record<string, string> = {
  admin:       'bg-red-900/40 border-red-800/50 text-red-200',
  operator:    'bg-blue-900/40 border-blue-800/50 text-blue-200',
  casier:      'bg-green-900/40 border-green-800/50 text-green-200',
  vizualizare: 'bg-slate-800 border-slate-700 text-slate-400',
}

function MobileTopBar({ onMenuOpen }: { onMenuOpen: () => void }) {
  const { page } = useNav()
  const { session } = useAuthStore()

  const initials = session?.numeComplet
    ?.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() ?? '?'
  const avatarClass = ROL_AVATAR[session?.rol ?? ''] ?? 'bg-slate-800 border-slate-700 text-slate-300'

  return (
    <header className="lg:hidden flex items-center gap-3 px-4 h-14 bg-slate-950 border-b border-slate-800/60 shrink-0">
      <button
        onClick={onMenuOpen}
        className="p-2 -ml-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/80 transition-colors active:scale-95"
        aria-label="Deschide meniu"
      >
        <Menu size={21} />
      </button>

      <p className="flex-1 text-[15px] font-semibold text-white truncate">
        {PAGE_LABELS[page] ?? page}
      </p>

      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold border ${avatarClass}`}>
        {initials}
      </div>
    </header>
  )
}

// ─── App Inner ─────────────────────────────────────────────────────────────

function isUnauthorized(error: any): boolean {
  const code = error?.data?.code ?? error?.message ?? ''
  return code === 'UNAUTHORIZED' || String(code).toLowerCase().includes('unauthorized')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, throwOnError: false },
    mutations: {
      onError: (error: any) => {
        if (isUnauthorized(error)) useAuthStore.getState().logout()
      },
    },
  },
})

queryClient.getQueryCache().subscribe(event => {
  if (event.type === 'updated' && event.query.state.status === 'error') {
    if (isUnauthorized(event.query.state.error)) {
      useAuthStore.getState().logout()
    }
  }
})

// La logout, golim tot cache-ul React Query — previne scurgerea datelor între sesiuni/utilizatori
registerLogoutCleanup(() => queryClient.clear())

function AppInner() {
  const { session } = useAuthStore()
  const { navigate } = useNav()
  const [showSearch, setShowSearch] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Emite un token de download restrâns (scope 'file') pentru URL-urile /files?t= —
  // dacă e scurs în loguri NU dă acces la tRPC/sesiune (audit C-sec2). TTL 2h.
  const downloadTokenQ = trpc.auth.getDownloadToken.useQuery(undefined, {
    enabled: !!session,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 90 * 60 * 1000,
  })
  useEffect(() => {
    if (downloadTokenQ.data?.token) localStorage.setItem('fileToken', downloadTokenQ.data.token)
  }, [downloadTokenQ.data])

  // Blochează scroll-ul body cât drawer-ul e deschis pe mobil
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [drawerOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        if (session) setShowSearch(s => !s)
        return
      }
      if (e.key === 'Escape') {
        setShowSearch(false)
        setDrawerOpen(false)
        return
      }
      if ((e.ctrlKey || e.metaKey) && !isInput) {
        const target = PAGE_SHORTCUTS[e.key]
        if (target) {
          // Nu naviga peste un wizard/modal deschis — ar demonta pagina și ar pierde datele
          // nesalvate din formular. Prezența unui overlay (.z-50/.z-[60]) blochează scurtătura.
          if (document.querySelector('.fixed.inset-0.z-50, .fixed.inset-0.z-\\[60\\], .fixed.inset-0.z-\\[70\\]')) return
          e.preventDefault()
          navigate(target)
          setDrawerOpen(false)
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate, session])

  if (!session) return <LoginScreen />

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-950">

      {/* Header vizibil doar pe mobil/tabletă */}
      <MobileTopBar onMenuOpen={() => setDrawerOpen(true)} />

      {/* Corp principal: sidebar + content */}
      <div className="flex-1 flex overflow-hidden min-h-0">

        {/* Sidebar — drawer pe mobil, static pe desktop */}
        <Sidebar
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSearchOpen={() => setShowSearch(true)}
        />

        {/* Overlay întunecat pe mobil cât drawer-ul e deschis */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
        )}

        {/* Conținut pagini */}
        <main className="flex-1 overflow-auto bg-slate-950 min-h-0">
          <Suspense fallback={
            <div className="flex items-center justify-center h-full text-slate-500">
              <div className="w-6 h-6 border-2 border-slate-700 border-t-brand-500 rounded-full animate-spin" />
            </div>
          }>
            <PageRouter />
          </Suspense>
        </main>
      </div>

      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
    </div>
  )
}

export default function App() {
  const [trpcClient] = useState(() => createTrpcClient())

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ThemeProvider>
            <AppInner />
          </ThemeProvider>
        </ToastProvider>
      </QueryClientProvider>
    </trpc.Provider>
  )
}
